import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    InMemoryCache,
    split
} from '@apollo/client/core';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { setContext } from '@apollo/client/link/context';
import { useSessionStore } from '@/stores/session';
import possibleTypes from '../graphql/possibleTypes_generated.json';
import servicePossibleTypes from '../graphql/service_possibleTypes_generated.json';
import type { UserModule } from '../types';
import { ApolloClients } from '@vue/apollo-composable';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ContextType } from '@/graphql/types-service';

function getV1Uri() {
    if (import.meta?.env) {
        return import.meta?.env.DEV
            ? '/api/graphql'
            : import.meta.env.BASE_URL + '/api/graphql';
    }
    return '/api/graphql';
}

function getV1WsUri() {
    const url = typeof location !== 'undefined' ? location.origin : '';
    if (url.startsWith('https')) {
        return url.replace('https', 'wss') + '/api/graphql/ws';
    } else {
        return url.replace('http', 'ws') + '/api/graphql/ws';
    }
}

function getHistoryUri() {
    if (import.meta?.env) {
        return import.meta?.env.DEV
            ? '/api/graphql/history'
            : import.meta.env.BASE_URL + '/api/graphql/history';
    }
    return '/api/graphql/history';
}

function getServiceUri() {
    if (import.meta?.env) {
        return import.meta?.env.DEV
            ? '/api/graphql/service'
            : import.meta.env.BASE_URL + '/api/graphql/service';
    }
    return '/api/graphql/service';
}

function getOptimizeUri() {
    if (import.meta?.env) {
        return import.meta?.env.DEV
            ? '/api/graphql/optimize'
            : import.meta.env.BASE_URL + '/api/graphql/optimize';
    }
    return '/api/graphql/optimize';
}

function getFileUri() {
    if (import.meta?.env) {
        return import.meta?.env.DEV
            ? '/api/graphql/file'
            : import.meta.env.BASE_URL + '/api/graphql/file';
    }
    return '/api/graphql/file';
}

const V1HttpLink = createUploadLink({
    uri: getV1Uri(),
    includeExtensions: true,
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, init)) as never
});

const wsLink = new GraphQLWsLink(
    createClient({
        webSocketImpl: typeof location !== 'undefined' ? WebSocket : undefined,
        url: getV1WsUri(),
        connectionParams: async () => {
            const sessionStore = useSessionStore();
            const token = sessionStore.getToken();
            return {
                token
            };
        }
    })
);

const historyHttpLink = createHttpLink({
    uri: getHistoryUri(),
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, init)) as never
});

const serviceHttpLink = createUploadLink({
    uri: getServiceUri(),
    includeExtensions: true,
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, init)) as never
});

const optimizeHttpLink = createHttpLink({
    uri: getOptimizeUri(),
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, init)) as never
});

const fileHttpLink = createUploadLink({
    uri: getFileUri(),
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, init)) as never
});

function adminCtxPrefix(ty: ContextType): string | undefined {
    let result;
    switch (ty) {
        case ContextType.Olb:
            result = 'Olb';
            break;
        case ContextType.OlbB:
            result = 'OlbB';
            break;
        case ContextType.Hdp:
            result = 'Hdp';
            break;
        case ContextType.Lab:
            result = 'Lab';
            break;
        case ContextType.LabB:
            result = 'LabB';
            break;
        case ContextType.Wsp:
            result = 'Wsp';
            break;
        case ContextType.WspB:
            result = 'WspB';
            break;
        default:
            break;
    }
    return result;
}

const authLink = new ApolloLink((op, forward) => {
    const sessionStore = useSessionStore();
    op.extensions = op.extensions || {};
    const ignoreAdminCtx = op.getContext()?.ignoreAdminCtx ?? false;
    if (sessionStore.sessionContext.isAdminOrSupport && !ignoreAdminCtx) {
        if (
            sessionStore.selectedContext.sub &&
            sessionStore.selectedContext.sub.id
        ) {
            const prefix = adminCtxPrefix(sessionStore.selectedContext.sub.ty);
            if (prefix) {
                op.extensions['admin'] =
                    `${prefix}:access@${sessionStore.selectedContext.sub.id}`;
            }
        } else if (
            sessionStore.selectedContext.root &&
            sessionStore.selectedContext.root.id
        ) {
            const prefix = adminCtxPrefix(sessionStore.selectedContext.root.ty);
            if (prefix) {
                op.extensions['admin'] =
                    `${prefix}:access@${sessionStore.selectedContext.root.id}`;
            }
        }
    }
    return forward(op);
}).concat(
    setContext(async (v, { headers }) => {
        const sessionStore = useSessionStore();
        const token = sessionStore.getToken();
        const result = {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : ''
            }
        };
        return result;
    })
);

const wsAuthLink = setContext(async (_, { options }) => {
    const sessionStore = useSessionStore();
    const token = sessionStore.getToken();
    return {
        options: {
            ...options,
            connectionParams: {
                authToken: token
            }
        }
    };
});

const v1Link = split(
    ({ query }) => {
        const definition = getMainDefinition(query);

        const result =
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription';

        return result;
    },
    wsAuthLink.concat(wsLink as unknown as ApolloLink),
    authLink.concat(V1HttpLink as unknown as ApolloLink)
);

export const apolloV1Client = new ApolloClient({
    link: v1Link,
    cache: new InMemoryCache({
        possibleTypes
    })
});

export const apolloHistoryClient = new ApolloClient({
    link: authLink.concat(historyHttpLink as unknown as ApolloLink),
    cache: new InMemoryCache({
        possibleTypes
    })
});

export const apolloServiceClient = new ApolloClient({
    link: authLink.concat(serviceHttpLink as unknown as ApolloLink),
    cache: new InMemoryCache({
        possibleTypes: servicePossibleTypes
    })
});

export const apolloOptimizeClient = new ApolloClient({
    link: authLink.concat(optimizeHttpLink as unknown as ApolloLink),
    cache: new InMemoryCache({
        possibleTypes
    })
});

export const apolloFileClient = new ApolloClient({
    link: authLink.concat(fileHttpLink as unknown as ApolloLink),
    cache: new InMemoryCache({
        possibleTypes
    })
});

export enum ApolloFetchPolicy {
    NetworkOnly = 'network-only',
    CacheFirst = 'cache-first'
}

export const HistoryApolloClient = Symbol('history-apollo-client');

export const install: UserModule = ({ app }) => {
    app.provide(ApolloClients, {
        default: apolloV1Client,
        history: apolloHistoryClient,
        service: apolloServiceClient,
        optimize: apolloOptimizeClient,
        file: apolloFileClient
    });
};
